'use client';

import { useState, useMemo } from 'react';
import { Minus, Plus, ShoppingCart, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from '@/components/ui/sheet';
import { useUser } from '../../contexts/UserContext';
import { useCart } from '../../hooks/useCart';
import { useNavigate } from 'react-router-dom';

export function CartDrawer({ product, isOpen, onClose, isCartBlocked = false, category = '' }) {
  const { cart, addToCart, updateQuantity, removeFromCart } = useCart();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { user } = useUser();
  const navigate = useNavigate();

  // Handle product images - could be a string or an array
  const productImages = useMemo(() => {
    if (!product) return [];
    if (Array.isArray(product.productImageUrl)) {
      return product.productImageUrl;
    }
    return [product.productImageUrl];
  }, [product]);

  const isDiscounted = product?.mrp > product?.netPrice;
  const discountPercentage = isDiscounted
    ? Math.round(((product.mrp - product.netPrice) / product.mrp) * 100)
    : 0;

  const handleQuantityChange = (variant, change) => {
    if (change > 0) {
      // Add the variant to cart
      addToCart({
        id: product.productId,
        name: product.productName,
        image: product.productImageUrl,
        mrp: variant.mrp || product.mrp,
        price: variant.netPrice || product.netPrice,
        unit: product?.baseUnit,
        quantity: 1,
        variant: variant,
      });
    } else {
      updateQuantity(product.productId, '', change);
    }
  };

  const cartItems = useMemo(() => {
    if (!product) return [];
    return cart.filter((item) => item.id === product.productId);
  }, [cart, product]);

  const calculateCartTotal = useMemo(() => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  }, [cartItems]);

  const getItemQuantity = (productId) => {
    const item = cartItems.find((item) => item.id === productId);
    console.log(item);
    return item ? item.quantity : 0;
  };

  const handleGoToCart = () => {
    onClose();
    navigate('/cart');
  };

  if (!product) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="max-w-md mx-auto py-0 max-h-[92%] px-0 rounded-t-3xl">
        <div className="flex flex-col overflow-auto max-h-screen h-full">
          {/* Header with close button */}
          <SheetHeader className="px-4 py-1 border-b rounded-t-3xl sticky top-0 bg-white z-10">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-xl font-bold">{product.productName}</SheetTitle>
              <SheetClose asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </SheetClose>
            </div>
          </SheetHeader>

          <div className="flex-1  px-3 py-1">
            {/* Main product image */}
            <div className="relative bg-gray-50 rounded-lg  mb-4">
              <img
                src={productImages[selectedImageIndex] || '/placeholder.svg'}
                alt={`${product.productName} - main view`}
                className="w-full h-44 object-contain"
              />

              {/* Availability badge */}
              {!product.available && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Badge variant="destructive" className="text-lg py-1.5 px-3">
                    Out of Stock
                  </Badge>
                </div>
              )}

              {/* Discount badge */}
              {isDiscounted && (
                <Badge className="absolute top-3 left-3 bg-green-600 hover:bg-green-700">
                  {discountPercentage}% OFF
                </Badge>
              )}
            </div>

            {/* Image gallery - all images in a single view */}
            {productImages.length > 1 && (
              <div className="grid grid-cols-4 gap-2 mb-2">
                {productImages.map((image, index) => (
                  <button
                    key={index}
                    className={`h-12 w-12 rounded-md overflow-hidden border transition-all ${
                      index === selectedImageIndex ? 'border-primary' : 'border-transparent'
                    }`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img
                      src={image || '/placeholder.svg'}
                      alt={`${product.productName} - view ${index + 1}`}
                      className="w-12 h-12 object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Product details */}
            <div className="space-y-2">
              {/* Price information */}
              <div className="space-y-2">
                <div className="flex items-baseline gap-2">
                  <span className={`text-2xl font-bold ${isDiscounted ? 'text-green-600' : ''}`}>
                    ₹{product.netPrice.toFixed(2)}
                  </span>
                  {isDiscounted && (
                    <span className="text-lg text-gray-500 line-through">
                      ₹{product.mrp.toFixed(2)}
                    </span>
                  )}
                  {!isDiscounted && (
                    <span className="text-sm text-gray-500">
                      MRP: ₹<span className="line-through">{product.mrp.toFixed(2)}</span>
                    </span>
                  )}
                </div>

                {isDiscounted && (
                  <p className="text-sm text-green-600">
                    You save: ₹{(product.mrp - product.netPrice).toFixed(2)} ({discountPercentage}%)
                  </p>
                )}
              </div>

              {/* Base unit and quantity selector */}
              <div>
                <p className="text-sm text-gray-500 mb-3">Base Unit: {product.baseUnit}</p>

                {user?.userType === 'user' && (
                  <div className="flex items-center">
                    <span className="mr-4 font-medium">Quantity:</span>
                    <div className="flex items-center border rounded-md">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleQuantityChange(1, -1)}
                        disabled={getItemQuantity(product?.productId) < 1 || !product.available}
                        className="h-10 w-10 rounded-none rounded-l-md"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <div className="w-12 text-center font-medium">
                        {getItemQuantity(product?.productId)}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleQuantityChange(1, 1)}
                        disabled={!product.available}
                        className="h-10 w-10 rounded-none rounded-r-md"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Total price calculation */}
              <div className="py-3 border-t border-b">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total:</span>
                  <span className="font-bold text-lg">₹{calculateCartTotal.toFixed(2)}</span>
                </div>
                {isDiscounted && (
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-gray-500">MRP Total:</span>
                    <span className="text-sm text-gray-500 line-through">
                      ₹{(product.mrp * getItemQuantity(product?.productId)).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons - fixed at bottom */}
          <div className="border-t bg-white px-4 py-2 sticky bottom-0">
            <div className="flex items-center gap-2">
              <Button
                className="w-full bg-gray-900 hover:bg-gray-800 text-white py-4 cursor-pointer"
                onClick={() => onClose()}
              >
                Continue
              </Button>
              {user?.userType === 'user' && (
                <Button
                  className="w-full bg-[#39c55e] hover:bg-[#2ea34d] text-white py-4 cursor-pointer"
                  onClick={handleGoToCart}
                  disabled={calculateCartTotal === 0 || isCartBlocked}
                >
                  {!isCartBlocked ? 'Go To Cart' : 'Not Available'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
